import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getYear } from "date-fns";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { Accordion } from "components/ui/Accordion";
import { Button } from "components/ui/Button";
import { Checkbox as CustomCheckbox } from "components/ui/Checkbox";
import { Select } from "components/ui/Select/Select";
import {
  CDM_CORRECTION_REASON_OPTIONS,
  CDM_DEFAULT_CORRECTION_REASON,
} from "features/cdm/constants/correctionReasonOptions";
import { useEthnicGroups } from "features/patients/hooks/useEthnicGroups";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { resolveEthnicityCode } from "features/patients/utils";
import { useStaffMembers } from "features/practice/hooks/useStaffMembers";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { formatEircode, validateEircode } from "utils/validators";

import { diagnosisItems } from "../../constants/diagnosisItems";
import { otherMajorDiagnosisItems } from "../../constants/otherMajorDiagnosisItems";
import style from "../AddCDMReviewDialog/AddCDMReviewDialog.module.css";

const editSchema = z.object({
  consultationDate: z.string().min(1, { message: "Please provide consultation date." }),
  eircode: z
    .string()
    .min(1, { message: "Please provide eircode." })
    .refine(
      (val) => validateEircode(val).isValid,
      (val) => ({ message: validateEircode(val).error || "Invalid Eircode format" })
    ),
  ethnicityCode: z.string().optional(),
  contractDoctor: z.string().optional(),
  diabetesType2: z.boolean().optional().default(false),
  asthma: z.boolean().optional().default(false),
  copd: z.boolean().optional().default(false),
  heartFailure: z.boolean().optional().default(false),
  atrialFibrillation: z.boolean().optional().default(false),
  ischaemicHeartDisease: z.boolean().optional().default(false),
  stroke: z.boolean().optional().default(false),
  tia: z.boolean().optional().default(false),
  diabetesType2Year: z.string().optional(),
  asthmaYear: z.string().optional(),
  copdYear: z.string().optional(),
  heartFailureYear: z.string().optional(),
  atrialFibrillationYear: z.string().optional(),
  ischaemicHeartDiseaseYear: z.string().optional(),
  strokeYear: z.string().optional(),
  tiaYear: z.string().optional(),
  diabetesType2AttendingHospital: z.boolean().optional().default(false),
  asthmaAttendingHospital: z.boolean().optional().default(false),
  copdAttendingHospital: z.boolean().optional().default(false),
  heartFailureAttendingHospital: z.boolean().optional().default(false),
  atrialFibrillationAttendingHospital: z.boolean().optional().default(false),
  ischaemicHeartDiseaseAttendingHospital: z.boolean().optional().default(false),
  strokeAttendingHospital: z.boolean().optional().default(false),
  tiaAttendingHospital: z.boolean().optional().default(false),
  myocardialInfarction: z.boolean().optional().default(false),
  dementia: z.boolean().optional().default(false),
  cancerStage2Plus: z.boolean().optional().default(false),
  seriousMobilityIssues: z.boolean().optional().default(false),
  chronicKidneyDisease: z.boolean().optional().default(false),
  significantMentalIllness: z.boolean().optional().default(false),
  hypertensionOnTreatment: z.boolean().optional().default(false),
  inflammatoryArthritis: z.boolean().optional().default(false),
  otherMajorDiagnosisOtherText: z.string().optional(),
});

export const CdmInitialStep = ({ data, onContinue, onCancel, submitDisabled = false }) => {
  const { data: staffMembers } = useStaffMembers();
  const { data: patient } = usePatientData();
  const { data: ethnicGroups } = useEthnicGroups();

  // Editing an already-submitted review is a correction (HL7 OBR.25=C).
  const isCorrection = !!data?.reimbursementMessageControlId;

  const currentYear = new Date().getFullYear().toString();

  const diagnosisDefaults = diagnosisItems.reduce((acc, item) => {
    const isChecked = data?.registrationReasons?.includes(item.id) ?? false;
    return {
      ...acc,
      [item.field]: isChecked,
      [`${item.field}Year`]: String(data?.yearOfDiagnosis?.[item.id] ?? currentYear),
      [`${item.field}AttendingHospital`]: data?.attendingHospital?.[item.id] ?? false,
    };
  }, {});

  const otherMajorDiagnosisDefaults = otherMajorDiagnosisItems.reduce(
    (acc, item) => ({
      ...acc,
      [item.field]:
        data?.otherMajorDiagnoses?.diagnoses?.includes(item.id) ?? false,
    }),
    {}
  );

  const form = useForm({
    resolver: zodResolver(editSchema),
    defaultValues: {
      consultationDate: data?.consultationDate ?? "",
      eircode: data?.eircode ?? "",
      contractDoctor: "",
      ethnicityCode: data?.ethnicityCode ?? "",
      ...diagnosisDefaults,
      ...otherMajorDiagnosisDefaults,
      otherMajorDiagnosisOtherText: data?.otherMajorDiagnoses?.otherText ?? "",
    },
  });

  useEffect(() => {
    // On edit keep the eircode submitted with the previous review; only fall
    // back to the patient record for a new review (no saved value).
    if (!data?.eircode && patient?.address?.postCode) {
      form.setValue("eircode", formatEircode(patient.address.postCode) || patient.address.postCode);
    }
  }, [data?.eircode, patient?.address?.postCode, form]);

  useEffect(() => {
    // Prefill ethnicity review-first (its value is a description enum), then
    // fall back to the patient file (a code). Normalise either shape to a code
    // so the Select — which matches by `g.code` — displays it, and the submitted
    // value stays a code (unchanged from the manual-select path).
    if (!ethnicGroups?.length) return;

    const source = data?.ethnicityCode || patient?.ethnicityCode;
    const code = resolveEthnicityCode(source, ethnicGroups);

    if (code) {
      form.setValue("ethnicityCode", code);
    }
  }, [data?.ethnicityCode, patient?.ethnicityCode, ethnicGroups, form]);

  useEffect(() => {
    if (staffMembers && data?.reviewingDoctor) {
      const match = staffMembers.find(
        (s) => `${s.firstName} ${s.lastName}` === data.reviewingDoctor
      );
      if (match) {
        form.setValue("contractDoctor", String(match.id));
      }
    }
  }, [staffMembers, data?.reviewingDoctor, form]);

  const handleEircodeBlur = (event) => {
    const val = event.target.value;

    if (val) {
      form.setValue("eircode", formatEircode(val), { shouldValidate: true });
    }
  };

  const handleFormSubmit = (formData) => {
    const checkedItems = diagnosisItems.filter((item) => formData[item.field]);
    const checkedIds = checkedItems.map((item) => item.id);

    const yearOfDiagnosis = checkedItems.reduce((acc, item) => {
      acc[item.id] = Number(formData[`${item.field}Year`]);
      return acc;
    }, {});

    const attendingHospital = checkedItems.reduce((acc, item) => {
      acc[item.id] = formData[`${item.field}AttendingHospital`] ?? false;
      return acc;
    }, {});

    const otherMajorDiagnoses = {
      diagnoses: otherMajorDiagnosisItems
        .filter((item) => formData[item.field])
        .map((item) => item.id),
      otherText: formData.otherMajorDiagnosisOtherText?.trim() || "",
    };

    // A correction (HL7 OBR.25=C) must hold the original treatment date
    // (GP Contract spec, Enhancement 27: "An amended review will hold the
    // original treatment date"). The consultation date is read-only here, so
    // never recompute reviewDate from the form — always carry the original.
    onContinue({
      registrationReasons: checkedIds,
      yearOfDiagnosis,
      attendingHospital,
      otherMajorDiagnoses,
      reviewDate: data?.reviewDate,
      consultationDate: data?.consultationDate ?? formData.consultationDate,
      ethnicityCode: formData.ethnicityCode,
      eircode: formData.eircode ? formData.eircode.replace(/\s+/g, "") : formData.eircode,
      contractDoctor: formData.contractDoctor,
    });
  };

  return (
    <FormProvider {...form}>
      <form
        className={style.addReferralForm}
        style={{ borderTop: "none", paddingTop: 0, marginTop: 0, gap: 0 }}
        onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("CdmInitialStep"))}
      >
      <div style={{ backgroundColor: "var(--gray-50)", paddingTop: "24px", paddingBottom: "24px" }}>
        <div
          className={style.inputGroupGrid4Columns}
          style={{
            padding: "0 24px",
            ...(isCorrection && { gridTemplateColumns: "repeat(5, 1fr)" }),
          }}
        >
          {isCorrection && (
            <FormField name="correctionReason">
              <label className={style.addReferralFormLabel}>
                Reason for correction
              </label>
              <Select
                options={CDM_CORRECTION_REASON_OPTIONS}
                value={CDM_DEFAULT_CORRECTION_REASON}
                onChange={() => {}}
                disabled
              />
            </FormField>
          )}
          <FormField name="consultationDate">
            <label className={style.pregnancyFormLabel}>Consultation date</label>
            <input
              {...form.register("consultationDate")}
              type="text"
              className="form-control"
              placeholder="dd-MM-yyyy"
              readOnly
              disabled
              title="A correction keeps the original review date — it cannot be changed."
            />
          </FormField>
          <FormField name="contractDoctor">
            <label className={style.addReferralFormLabel}>Contract Doctor</label>
            <Select
              options={(staffMembers ?? []).map((staff) => ({
                value: staff.id,
                label: `${staff.firstName} ${staff.lastName}${staff.staffRole ? ` (${staff.staffRole.toLowerCase()})` : ""}${staff.active ? "" : " (inactive)"}`,
              }))}
              value={form.watch("contractDoctor") ?? ""}
              onChange={(value) => form.setValue("contractDoctor", value, { shouldValidate: true })}
            />
          </FormField>
          <FormField name="ethnicityCode">
            <label className={style.addReferralFormLabel}>Ethnicity</label>
            <Select
              options={(ethnicGroups ?? []).map((group) => group.description)}
              value={ethnicGroups?.find((g) => g.code === form.watch("ethnicityCode"))?.description ?? ""}
              onChange={(value) => {
                const selectedGroup = ethnicGroups?.find((g) => g.description === value);
                form.setValue("ethnicityCode", selectedGroup?.code ?? "", { shouldValidate: true });
              }}
            />
          </FormField>
          <FormField name="eircode">
            <label className={style.pregnancyFormLabel}>Eircode</label>
            <input
              type="text"
              className="form-control"
              {...form.register("eircode")}
              onBlur={handleEircodeBlur}
            />
          </FormField>
        </div>
        <div className={style.indicatorsSection} style={{ paddingLeft: "24px", paddingRight: "24px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "bold" }}>Diagnosis</h2>
          <div className={style.tableContainer}>
            <table className={style.table}>
              <colgroup>
                <col style={{ width: "30px" }} />
                <col />
                <col style={{ width: "180px" }} />
                <col style={{ width: "180px" }} />
              </colgroup>
              <thead className={style.thead}>
                <tr>
                  <th className={style.th} />
                  <th className={style.th}>Reason</th>
                  <th className={style.th}>Attending Hospital</th>
                  <th className={style.th}>Year</th>
                </tr>
              </thead>
              <tbody>
                {diagnosisItems.map((item) => (
                  <tr key={item.id}>
                    <td className={style.td}>
                      <CustomCheckbox
                        name={item.field}
                        checked={form.watch(item.field)}
                        onChange={(event) => form.setValue(item.field, event.target.checked)}
                      />
                    </td>
                    <td className={style.td}>{item.name}</td>
                    <td className={style.td}>
                      <CustomCheckbox
                        name={`${item.field}AttendingHospital`}
                        checked={form.watch(`${item.field}AttendingHospital`) ?? false}
                        disabled={!form.watch(item.field)}
                        onChange={(event) =>
                          form.setValue(`${item.field}AttendingHospital`, event.target.checked)
                        }
                      />
                    </td>
                    <td className={style.td}>
                      <Select
                        small
                        options={Array.from({ length: 150 }, (_, i) => getYear(new Date()) - i).map((year) => ({
                          value: year,
                          label: year,
                        }))}
                        disabled={!form.watch(item.field)}
                        value={form.watch(`${item.field}Year`) ?? ""}
                        onChange={(value) => form.setValue(`${item.field}Year`, value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div
          className={`${style.indicatorsSection} ${style.noDivider}`}
          style={{ paddingLeft: "24px", paddingRight: "24px" }}
        >
          <Accordion
            items={[
              {
                id: "otherMajorDiagnoses",
                title: "Other Major Diagnoses",
                content: (
                  <>
                    <div className={style.diagnosisList}>
                      {otherMajorDiagnosisItems.map((item) => (
                        <label
                          key={item.id}
                          className={style.diagnosisListItem}
                        >
                          <CustomCheckbox
                            name={item.field}
                            checked={form.watch(item.field)}
                            onChange={(event) =>
                              form.setValue(item.field, event.target.checked)
                            }
                          />
                          <span>{item.name}</span>
                        </label>
                      ))}
                    </div>
                    <FormField name="otherMajorDiagnosisOtherText">
                      <label
                        className={`${style.addReferralFormLabel} ${style.otherFreeTextLabel}`}
                      >
                        Other (free text)
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        {...form.register("otherMajorDiagnosisOtherText")}
                      />
                    </FormField>
                  </>
                ),
              },
            ]}
            allowMultiple={false}
            className={style.plainAccordion}
          />
        </div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: "20px", padding: "24px", borderTop: "1px solid var(--gray-200)" }}>
        <Button variant="outline" type="button" onClick={onCancel} style={{ width: "200px" }}>Back</Button>
        <Button type="submit" disabled={submitDisabled} style={{ width: "200px" }}>Continue</Button>
      </div>
      </form>
    </FormProvider>
  );
};
